// PostHog for the web app, reporting into the same project as the marketing
// site (one token, so the `ph_<token>_posthog` cookie on `.itsbaba.com` carries
// the visitor's anonymous id from www.itsbaba.com into app.itsbaba.com and the
// landing-to-signup-to-purchase funnel is one person).
//
// posthog-js drops any capture() made before init(), and init is deferred
// until the browser is idle so it never competes with LCP. The Google sign-in
// handoff used to fire sign_up / login in a mount effect and lose them that
// way. Everything now goes through phCapture(), which queues until the client
// has loaded and replays with the original timestamps.
//
// Identity: the distinct id is the gateway's stable_user_id (the same identity
// the phone uses), never the email. An email can change; the id cannot, and an
// id that was identified once cannot be merged into a different person later.
//
// This mirrors apps/landing/lib/posthog.ts; the two differ only in how they
// read env and in what is switched on (no autocapture, heatmaps or exception
// capture here: the app is interaction-heavy and Sentry owns its errors).

import type { CaptureResult, PostHogConfig, Properties } from 'posthog-js'
import { scrubAnalyticsUrl } from './analyticsUrl'

type Queued = { event: string; properties?: Properties; timestamp: Date }
// What `loaded` hands us: the public surface, not the class.
type LoadedClient = Parameters<NonNullable<PostHogConfig['loaded']>>[0]

const QUEUE_LIMIT = 100
const queue: Queued[] = []
let client: LoadedClient | null = null
let initStarted = false
let pendingIdentify: { id: string; properties?: Properties } | null = null
let pendingReset = false
let pendingRecording = false

/**
 * Events that must survive a full page navigation. The sign-in handlers fire
 * sign_up / login and then reload or replace the document, so an in-memory
 * queue would die with the page; these wait in sessionStorage instead and
 * are sent by the next document once the client is up.
 */
const DURABLE_KEY = 'baba.ph.pending'

/**
 * Remove sign-in tokens, session JWTs, emails and codes from the query string
 * AND the hash of a URL or a path, and replace secret path segments (the
 * email claim token, a shared chat's token) with a route template. The hash
 * matters here: the magic link lands on `#token=...&email=...` and waits
 * behind a button, and the Google handoff lands on `#session=<jwt>&email=...`,
 * while posthog-js takes $current_url from the full location.href. The rules
 * live in lib/analyticsUrl.ts, shared with GA, Speed Insights and Sentry.
 */
export function scrubSensitiveParams(value: string): string {
  return scrubAnalyticsUrl(value)
}

/**
 * Scrub every string anywhere inside a property bag, a few levels deep.
 * posthog-js nests URLs: $set_once.$initial_person_info.u, and each
 * $web_vitals_*_event carries its own navigationURL.
 */
function scrubStrings(bag: unknown, depth = 0): void {
  if (!bag || typeof bag !== 'object' || depth > 4) return
  const record = bag as Record<string, unknown>
  for (const key of Object.keys(record)) {
    const value = record[key]
    if (typeof value === 'string') record[key] = scrubSensitiveParams(value)
    else if (value && typeof value === 'object') scrubStrings(value, depth + 1)
  }
}

/**
 * Strip sign-in material out of every string property that is a URL or a
 * path, on every event, including the $set / $set_once person properties
 * that carry the first URL a visitor arrived on and the bare $pathname.
 */
export function scrubSensitiveUrls(result: CaptureResult | null): CaptureResult | null {
  if (!result) return result
  scrubStrings(result.properties)
  scrubStrings(result.$set)
  scrubStrings(result.$set_once)
  return result
}

function isAutomatedBrowser(): boolean {
  const ua = navigator.userAgent || ''
  return navigator.webdriver === true || /HeadlessChrome/.test(ua)
}

function isLocalHost(): boolean {
  const host = window.location.hostname
  return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')
}

/** Capture now if the client is loaded, otherwise queue for replay on load. */
export function phCapture(event: string, properties?: Properties): void {
  if (typeof window === 'undefined') return
  if (client) {
    client.capture(event, properties)
    return
  }
  if (queue.length >= QUEUE_LIMIT) queue.shift()
  queue.push({ event, properties, timestamp: new Date() })
}

function readDurable(): Queued[] {
  try {
    const raw = sessionStorage.getItem(DURABLE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as Array<{ event: string; properties?: Properties; timestamp: string }>
    return parsed.map((item) => ({ ...item, timestamp: new Date(item.timestamp) }))
  } catch {
    return []
  }
}

/**
 * Like phCapture, but the event survives a reload or a location change. If
 * the client is already up it is sent at once; otherwise it is written to
 * sessionStorage and sent by whichever document loads the client next.
 */
export function phCaptureDurable(event: string, properties?: Properties): void {
  if (typeof window === 'undefined') return
  if (client) {
    client.capture(event, properties)
    return
  }
  try {
    const pending = readDurable()
    pending.push({ event, properties, timestamp: new Date() })
    sessionStorage.setItem(DURABLE_KEY, JSON.stringify(pending.slice(-QUEUE_LIMIT)))
  } catch {
    // No sessionStorage (private mode, blocked storage): fall back to the
    // in-memory queue and accept that a reload may lose it.
    phCapture(event, properties)
  }
}

/**
 * Attach the session to a person. Call with the gateway's stable_user_id; the
 * anonymous history in this browser (including the landing-site visit that
 * shares the cookie) merges into that person on the first call.
 */
export function phIdentify(id: string, properties?: Properties): void {
  if (typeof window === 'undefined' || !id) return
  if (client) {
    client.identify(id, properties)
    return
  }
  pendingIdentify = { id, properties }
}

/**
 * Sign-out: drop the person so the next account on this browser starts clean.
 * If the client is not up yet the reset runs first thing on load, before any
 * queued identify, so a fast sign-out never leaves the old person behind.
 */
export function phReset(): void {
  pendingIdentify = null
  if (client) {
    client.reset()
    return
  }
  pendingReset = true
}

/**
 * Replay is off by default and started on intent (paywall, onboarding) so the
 * recording quota goes where the signal is. Idempotent once the client is up.
 */
export function phStartRecording(): void {
  if (typeof window === 'undefined') return
  if (client) {
    client.startSessionRecording()
    return
  }
  pendingRecording = true
}

/**
 * Load and initialise PostHog. Safe to call more than once; only the first
 * call does anything. Resolves once the client is loaded and the queue has
 * been replayed.
 */
export async function initPostHog(): Promise<void> {
  if (typeof window === 'undefined' || initStarted) return
  initStarted = true

  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined
  const host = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://us.i.posthog.com'
  if (!key) return
  // Dev sessions and automation stay out of the production project.
  // VITE_POSTHOG_DEBUG=1 opts a local or Playwright-driven run back in.
  const debug = import.meta.env.VITE_POSTHOG_DEBUG === '1'
  if (!debug && (isAutomatedBrowser() || isLocalHost())) return

  const { default: posthog } = await import('posthog-js')

  await new Promise<void>((resolve) => {
    posthog.init(key, {
      api_host: host,
      // Same preset as the landing site so both apps write the shared cookie
      // in the same format: history-change pageviews, strict minimum replay
      // duration, head injection for lazy scripts, localhost tagged as internal.
      defaults: '2026-05-30',
      person_profiles: 'identified_only',
      capture_pageview: 'history_change',
      capture_pageleave: true,
      autocapture: false,
      capture_heatmaps: false,
      capture_dead_clicks: false,
      capture_exceptions: false,
      capture_performance: { web_vitals: true, network_timing: true },
      disable_session_recording: true,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '[data-private], [data-sensitive]',
      },
      // Persistence is localStorage+cookie and, by default, localStorage wins
      // when the two disagree. www and app have separate localStorage but
      // share the cookie on .itsbaba.com, so without this the app's identified
      // distinct id would be overwritten by a stale anonymous id from the
      // other origin and the cross-domain person would split. Same flag on
      // the landing site.
      __preview_cookie_wins_on_conflict: true,
      before_send: scrubSensitiveUrls,
      loaded: (instance) => {
        client = instance
        if (import.meta.env.DEV) instance.debug()

        if (pendingReset) {
          pendingReset = false
          instance.reset()
        }
        if (pendingIdentify) {
          instance.identify(pendingIdentify.id, pendingIdentify.properties)
          pendingIdentify = null
        }
        const durable = readDurable()
        if (durable.length) {
          try { sessionStorage.removeItem(DURABLE_KEY) } catch {}
        }
        for (const item of [...durable, ...queue.splice(0)]) {
          instance.capture(item.event, item.properties, { timestamp: item.timestamp })
        }
        if (pendingRecording) {
          pendingRecording = false
          instance.startSessionRecording()
        }
        resolve()
      },
    })
  })
}
